import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shopService } from '../../services/index.js';
import { LoadingSpinner } from '../../components/index.js';
import { ROUTES } from '../../utils/index.js';
import './Dashboard.css';

export const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [shopStats, setShopStats] = useState(null);
  const [showAllStates, setShowAllStates] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const statsRes = await shopService.getShopStats();
        setShopStats(statsRes.data.stats);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="loading-container-dashboard">
        <LoadingSpinner size="large" message="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container-dashboard">
        <p className="error-message-dashboard">{error}</p>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Shops',
      value: shopStats?.total || 0,
      color: 'blue',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      label: 'Geocoded',
      value: shopStats?.geocoded || 0,
      color: 'green',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
    },
    {
      label: 'Not Geocoded',
      value: shopStats?.notGeocoded || 0,
      color: 'amber',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
    },
    {
      label: 'States',
      value: shopStats?.byState?.length || 0,
      color: 'purple',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    },
  ];

  const stateData = shopStats?.byState || [];
  const maxCount = stateData.length > 0 ? Math.max(...stateData.map((s) => s.count)) : 0;
  const STATES_LIMIT = 8;
  const visibleStates = showAllStates ? stateData : stateData.slice(0, STATES_LIMIT);

  return (
    <div className="container-dashboard">
      {/* Breadcrumb */}
      <nav className="breadcrumbs-dashboard">
        <span className="breadcrumb-current-dashboard">Dashboard</span>
      </nav>

      {/* Header */}
      <header className="header-dashboard">
        <div className="header-left-dashboard">
          <div className="header-text-dashboard">
            <h1 className="greeting-dashboard">Postcards Marketing</h1>
            <p className="subtitle-dashboard">
              Here's your postcards marketing overview
            </p>
          </div>
        </div>
      </header>

      {/* Stat Cards */}
      <section className="stats-dashboard">
        {statCards.map((card, index) => (
          <div
            key={card.label}
            className={`stat-card-dashboard stat-${card.color}-dashboard`}
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <div className="stat-accent-dashboard" />
            <div className="stat-body-dashboard">
              <div className={`stat-icon-dashboard icon-${card.color}-dashboard`}>
                {card.icon}
              </div>
              <div className="stat-info-dashboard">
                <span className="stat-value-dashboard">{card.value}</span>
                <span className="stat-label-dashboard">{card.label}</span>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* States Distribution */}
      {stateData.length > 0 && (
        <section className="states-dashboard">
          <div className="states-header-dashboard">
            <h2 className="section-title-dashboard">Shops by State</h2>
            <span className="states-count-dashboard">{stateData.length} states</span>
          </div>
          <div className="states-list-dashboard">
            {visibleStates.map((state, index) => (
              <div
                key={state._id}
                className="state-row-dashboard"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <span className="state-name-dashboard">{state._id}</span>
                <div className="state-bar-wrapper-dashboard">
                  <div
                    className="state-bar-dashboard"
                    style={{ width: `${(state.count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="state-count-dashboard">{state.count}</span>
              </div>
            ))}
          </div>
          {stateData.length > STATES_LIMIT && (
            <button
              className="states-toggle-dashboard"
              onClick={() => setShowAllStates(!showAllStates)}
            >
              {showAllStates
                ? 'Show less'
                : `View all ${stateData.length} states`}
            </button>
          )}
        </section>
      )}

      {/* CTA */}
      <section className="cta-dashboard">
        <div className="cta-content-dashboard">
          <h2 className="cta-title-dashboard">Ready to send postcards?</h2>
          <p className="cta-text-dashboard">
            Browse your shops, search for addresses, and create mailing batches.
          </p>
        </div>
        <button
          className="cta-btn-dashboard"
          onClick={() => navigate(ROUTES.SHOPS)}
        >
          Go to Shops
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </section>
    </div>
  );
};

export default Dashboard;
